import { TweenMax, Expo, Quint } from 'gsap';
import 'gsap/ScrollToPlugin';

/**
 * Opens an element
 *
 * @param el - DOM element
 * @param speed - Speed of animation
 * @param ease - Ease function
 * @param onComplete - On complete callback
 */
export const open = (el, speed = 0.4, ease = 'easeOut', onComplete) => {
  TweenMax.set(el, { height: 'auto' });
  TweenMax.from(el, speed, { height: 0, ease: Expo[ease], onComplete });
};

/**
 * Closes an element
 *
 * @param el - DOM element
 * @param speed - Speed of animation
 * @param ease - Ease function
 * @param onComplete - On complete callback
 */
export const close = (el, speed = 0.4, ease = 'easeOut', onComplete) => {
  TweenMax.to(el, speed, { height: 0, ease: Expo[ease], onComplete });
};

/**
 * Returns query string parameters
 *
 * @param query - URL's query string
 */
export const getUrlParams = query => {
  if (!query) {
    return {};
  }

  return (/^[?#]/.test(query) ? query.slice(1) : query)
    .split('&')
    .reduce((params, param) => {
      const [key, value] = param.split('=');
      params[key] = value ? decodeURIComponent(value.replace(/\+/g, ' ')) : '';
      return params;
    }, {});
};

// Capture initial query parameters on page load to preserve tracking params (e.g., UTM)
const _initialSearchParams = window.location.search;

// Param keys managed by listing/search components — these should NOT be
// carried over from the initial URL when a component clears them.
const _managedParamKeys = new Set([
  'keyword', 'currentpage', 'pagesize',
  'dateRange', 'topic', 'topicname', 'topics',
  'solution', 'solutionname',
  'subtype', 'subtypename',
  'type', 'boostscore',
  'insight-type', 'insightsbytype',
  'timerange', 'timerangefrom', 'timerangeto',
  'format', 'area',
  'series', 'seriesnames',
  'person', 'personname',
  'position', 'location', 'experience',
  'letter', 'sort',
  'upcoming', 'past',
  'clecredit', 'firmevent',
  'group', 'insightslanding', 'external-subtype',
  'pagetype', 'pageid', 'people'
].map(key => key.toLowerCase()));

/**
 * Pushes url to history state
 * Warning: will replace current querystring in URL
 * Preserves initial page load query params not managed by any component (e.g., UTM tracking params)
 * @param newQueryString - Query string to be used in browser and history
 */
export const updateHistory = newQueryString => {
  const initialParams = getUrlParams(_initialSearchParams);
  const newParams = getUrlParams(newQueryString || '');

  // Start with only the non-managed initial params (e.g., UTM tracking params)
  const merged = {};
  Object.keys(initialParams).forEach(key => {
    if (!_managedParamKeys.has(key.toLowerCase()) && initialParams[key]) {
      merged[key] = initialParams[key];
    }
  });

  // Layer on the component's current params
  Object.keys(newParams).forEach(key => {
    if (newParams[key]) {
      merged[key] = newParams[key];
    }
  });

  const queryString = Object.keys(merged)
    .filter(key => merged[key])
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(merged[key])}`)
    .join('&');

  const newURL = `${location.origin}${location.pathname}${queryString ? '?' + queryString : ''}`;
  history.pushState({}, '', newURL);
};

/**
 * Gets the query string containing only the preserved (non-managed) params
 * Use this when a component needs to do a full page reload (e.g. window.location.search = getPreservedParamsString())
 * @returns {string} Query string starting with '?' or empty string
 */
export const getPreservedParamsString = () => {
  const initialParams = getUrlParams(_initialSearchParams);

  const merged = {};
  Object.keys(initialParams).forEach(key => {
    if (!_managedParamKeys.has(key.toLowerCase()) && initialParams[key]) {
      merged[key] = initialParams[key];
    }
  });

  const queryString = Object.keys(merged)
    .filter(key => merged[key])
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(merged[key])}`)
    .join('&');

  return queryString ? '?' + queryString : '';
};

/**
 * Scroll to point on a page
 *
 * @param y - Position to scroll to
 * @param speed - Speed of animation
 */
export const scrollTo = (y, speed = 1.5, onCompleteHandler) => {
  TweenMax.to(window, speed, { scrollTo: { y }, ease: Expo.easeOut, onComplete: onCompleteHandler });
};

/**
 * Initializes inline SVG's
 *
 * @param config - Configuration holding the DOM element and the target
 * Reference: https://stackoverflow.com/questions/24933430/img-src-svg-changing-the-fill-color
 */
export const initSVG = config => {
  $(config.el).each((k, v) => {
    const $img = $(v);
    const imgID = $img.attr('id');
    const imgClass = $img.attr('class');
    const imgURL = $img.attr('src');

    $.get(imgURL, data => {
      // Get the SVG tag, ignore the rest
      let $svg = $(data).find(config.target);

      // Add replaced image's ID to the new SVG
      if (typeof imgID !== 'undefined') {
        $svg = $svg.attr('id', imgID);
      }
      // Add replaced image's classes to the new SVG
      if (typeof imgClass !== 'undefined') {
        $svg = $svg.attr('class', imgClass + ' replaced-svg');
      }

      // Remove any invalid XML tags as per http://validator.w3.org
      $svg = $svg.removeAttr('xmlns:a');

      // Check if the viewport is set, else we gonna set it if we can.
      if (!$svg.attr('viewBox') && $svg.attr('height') && $svg.attr('width')) {
        $svg.attr('viewBox', '0 0 ' + $svg.attr('height') + ' ' + $svg.attr('width'));
      }

      // Replace image with new SVG
      $img.replaceWith($svg);

    }, 'xml');
  });
};

export const truncateString = (str, n) => {
  return str.substr(0, n - 1) + (str.length > n ? '&hellip;' : '');
};


/**
 * Parses a query string
 *
 * @param query - querystring to parse.
*/
export const getQueryParams = query => {
  const params = {};

  query.replace(/([^?=&]+)(=([^&]*))?/g,
    ($0, $1, $2, $3) => {
      const key = $1.toLowerCase();

      params[key] = decodeURIComponent($3);
    });

  return params;
};


/**
 * Returns paramenter value
 *
 * @param sParam - URL param
 */
export const getUrlParameter = (sParam, url) => {
  const sPageURL = window.location.search.substring(1);
  const sURLVariables = sPageURL.split('&');
  let sParameterName, i;

  for(i = 0; i < sURLVariables.length; i++) {
    sParameterName = sURLVariables[i].split('=');

    if (sParameterName[0] === sParam) {
      return sParameterName[1] === undefined ? true : decodeURIComponent(sParameterName[1]);
    }
  }
};

export function addLabelsToSelectric() {
  setTimeout(() => {
    const selectricWrappers = $('.selectric-wrapper')

    if (selectricWrappers) {
      $.each(selectricWrappers, (index, element) => {
        const label = $(element).find('.selectric').find('.label');
        const id = 'input-id-' + (Math.round(Math.random() * 10000));

        if (label) {
          const text = $(label).text();
          const hiddenInput = $(element).find('.selectric-input');

          if (hiddenInput.length) {
            $(hiddenInput).attr('id', id);
            $('<label class="visually-hidden" for="' + id + '">' + text + '</label>').insertBefore($(hiddenInput));
          }
        }
      });
    }
  }, 1000)
}

export const setScrollPosition = name => sessionStorage.setItem(`scrollToPrevPosition${name}`, $(window).scrollTop());

export const goToPreviewPosition = name => {
  const data = sessionStorage.getItem(`scrollToPrevPosition${name}`);

  if (data) {
    window.scrollTo({
      top: data
    });
    sessionStorage.removeItem(`scrollToPrevPosition${name}`)
  }
}


// /**
// * Checks browser is running on a native mobile device
// */
export const isMobileDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  return (
    /windows phone/i.test(userAgent) ||
    /android/i.test(userAgent) ||
    (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream)
  );
};

// /**
// * Checks iOS user agent
// */
export const iOS = () => {
  return (
    [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod'
    ].includes(navigator.platform) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
};

// /**
// * Returns if media query matches mobile
// */
export const isMobile = () => {
  return window.matchMedia('(max-width: 767px)').matches;
};

// /**
// * Returns if media query matches tabletLandscape
// */
export const isTabletLandscape = () => {
  return window.matchMedia('(max-width: 1023px)').matches;
};

// /**
// * Returns the browser's scrollbar width
// */
export const getScrollbarWidth = () => {
  return window.innerWidth - document.documentElement.clientWidth;
};

// /**
// * Checks if an element is focusable
// *
// * @param {Object} el - HTML element you want to check if it's focusable
// */
export const isFocusable = el => {
  if (
    el.tabIndex > 0 ||
    (el.tabIndex === 0 && el.getAttribute('tabIndex') !== null)
  ) {
    return true;
  }

  if (el.disabled) {
    return false;
  }

  /* eslint-disable indent */
  switch (el.nodeName) {
    case 'A':
      return !!el.href && el.rel !== 'ignore';
    case 'INPUT':
      return el.type !== 'hidden' && el.type !== 'file';
    case 'BUTTON':
    case 'SELECT':
    case 'TEXTAREA':
      return true;
    default:
      return false;
  }
  /* eslint-enable indent */
};

// /**
// * FOCUSABLE ELEMENTS
// */
export const focusableElements = [
  '[contenteditable]',
  '[tabindex]:not([tabindex^="-"])',
  'a[href]',
  'area[href]',
  'button:not([disabled]):not([aria-hidden])',
  'embed',
  'iframe',
  'input:not([disabled]):not([type="hidden"]):not([aria-hidden])',
  'object',
  'select:not([disabled]):not([aria-hidden])',
  'textarea:not([disabled]):not([aria-hidden])'
];

// * Check if an element is outside the viewport
// */
export const isOutOfViewport = elem => {
  // Get element's bounding
  const bounding = elem.getBoundingClientRect();

  // Check if it's out of the viewport on each side
  const out = {};
  out.top = bounding.top < 0;
  out.left = bounding.left < 0;
  out.bottom =
    bounding.bottom >
    (window.innerHeight || document.documentElement.clientHeight);
  out.right =
    bounding.right >
    (window.innerWidth || document.documentElement.clientWidth);
  out.any = out.top || out.left || out.bottom || out.right;
  out.all = out.top && out.left && out.bottom && out.right;

  return out;
};

export const numberWithCommas = x => x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const sendSearchGAEvent = (event, search_term, num_search_results) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    search_term,
    num_search_results
  });
}





// WEBPACK FOOTER //
// ./src/js/helpers/index.js